import { useMemo } from 'react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import { calculateTypicalDayProfile } from '../utils/consumptionProfile'
import './TypicalDayProfile.css'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

function TypicalDayProfile({ consumptionData }) {
  const profile = useMemo(() => {
    return calculateTypicalDayProfile(consumptionData)
  }, [consumptionData])

  if (!profile) return null

  const { hourlyStats, individualDays } = profile

  const chartData = {
    labels: hourlyStats.map(s => s.hour),
    datasets: [
      // 1. Mediana (pagrindinė linija)
      {
        label: 'Mediana (Tipinis vartojimas)',
        data: hourlyStats.map(s => s.p50),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderWidth: 4,
        pointRadius: 0,
        tension: 0.4,
        fill: false,
        order: 1
      },
      // 2. Normalaus vartojimo koridorius (P25 - P75)
      {
        label: 'P75 (Viršutinė riba)',
        data: hourlyStats.map(s => s.p75),
        borderColor: 'rgba(59, 130, 246, 0.4)',
        backgroundColor: 'rgba(59, 130, 246, 0.15)',
        borderWidth: 1,
        pointRadius: 0,
        tension: 0.4,
        fill: '+1', // Užpildo iki kito dataset (P25)
        order: 5
      },
      {
        label: 'P25 (Apatinė riba)',
        data: hourlyStats.map(s => s.p25),
        borderColor: 'rgba(59, 130, 246, 0.4)',
        backgroundColor: 'transparent',
        borderWidth: 1,
        pointRadius: 0,
        tension: 0.4,
        fill: false,
        order: 6
      },
      // 3. Anomalių pikai (P95)
      {
        label: 'P95 (Reti pikai / EV)',
        data: hourlyStats.map(s => s.p95),
        borderColor: '#ef4444',
        borderDash: [5, 5],
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
        fill: false,
        order: 2
      }
    ]
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#f8fafc'
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.raw.toFixed(3)} kWh`
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Suvartojimas (kWh)',
          color: '#94a3b8'
        },
        grid: {
          color: 'rgba(71, 85, 105, 0.2)'
        },
        ticks: {
          color: '#94a3b8'
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#94a3b8',
          maxRotation: 0
        }
      }
    }
  }

  return (
    <div className="typical-profile-card">
      <div className="profile-header">
        <div className="header-icon">📈</div>
        <div className="header-text">
          <h3>Tipinio dienos suvartojimo paveikslas</h3>
          <p>Statistinis modelis, atskiriantis įprastą elgseną nuo retų šuolių</p>
        </div>
      </div>
      <div className="chart-outer-container">
        <div className="chart-inner-container">
          <Line data={chartData} options={options} />
        </div>
      </div>
      <div className="profile-footer">
        <div className="legend-hint">
          <span className="dot p50"></span> Mediana (įprasta diena)
          <span className="dot corridor"></span> Koridorius (P25-P75)
          <span className="dot p95"></span> Reti pikai (P95)
        </div>
      </div>
    </div>
  )
}

export default TypicalDayProfile
